import { FormField } from '@/components/ui/Form';
import { Form, FormInput, FormItem, FormLabel, MultiSelect, Switch } from '@/components/wm';
import { cn, getOptionsFromJson, getStoreResource } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const defineName = data => {
    if (!data.ids || data.ids.length === 0) {
        return null;
    }
    if (data.name !== '') {
        return data.name;
    }

    const names = data.ids
        .map(id => {
            const alarm = getStoreResource('alarms', id);
            return alarm ? alarm.name : null;
        })
        .filter(Boolean); // Remove nulls or undefined

    if (names.length === 0) {
        return null;
    }

    // Count frequencies
    const counts = {};
    let maxCount = 0;
    let mostFrequentName = null;

    names.forEach(name => {
        counts[name] = (counts[name] || 0) + 1;
        if (counts[name] > maxCount) {
            maxCount = counts[name];
            mostFrequentName = name;
        }
    });

    return mostFrequentName;
};

const defineNotifications = data => {
    if (!data.ids || data.ids.length === 0) {
        return [];
    }
    if (data.userIds && data.userIds.length !== 0) {
        return data.userIds;
    }

    const userIdsList = data.ids
        .map(id => {
            const alarm = getStoreResource('alarms', id);
            return alarm ? alarm.user_ids : null;
        })
        .filter(ids => Array.isArray(ids)); // Keep only valid arrays

    if (userIdsList.length === 0) {
        return [];
    }

    // Count frequencies
    const counts = {};
    const keyToOriginalArray = {};
    let maxCount = 0;
    let mostFrequentKey = null;

    userIdsList.forEach(ids => {
        // Stringify sorted array to use as a reliable counting key
        const key = JSON.stringify([...ids].sort());

        counts[key] = (counts[key] || 0) + 1;

        // Save the original array so we can return it later
        if (!keyToOriginalArray[key]) {
            keyToOriginalArray[key] = ids;
        }

        // Track the highest count
        if (counts[key] > maxCount) {
            maxCount = counts[key];
            mostFrequentKey = key;
        }
    });

    return mostFrequentKey ? keyToOriginalArray[mostFrequentKey] : [];
};

export function Properties({ data, onChangeField, onCompleted }) {
    const mostCommonName = useMemo(() => defineName(data), [data.ids]);
    const mostCommonUserIds = useMemo(() => defineNotifications(data), [data.ids]);

    const formSchema = z.object({
        changeName: z.boolean().default(false),
        changeUserIds: z.boolean().default(false),
        name: z.string().optional(),
        userIds: z.array(z.string()).optional()
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            changeName: false,
            changeUserIds: false,
            name: '',
            userIds: []
        },
        mode: 'onChange'
    });

    const { watch, setValue, getValues } = form;
    const changeName = watch('changeName');
    const changeUserIds = watch('changeUserIds');

    const userOptions = useMemo(() => {
        const users = getStoreResource('users') || [];
        return getOptionsFromJson(users);
    }, []);

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    return (
        <Form {...form}>
            <div className="tw-space-y-6">
                {/* Name Property */}
                <div className="tw-flex tw-items-end tw-gap-4">
                    <div className="tw-flex-1">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem itemInfo={
                                    field.value === '' && changeName
                                        ? i18next.t('alarms.name_warning').capitalize()
                                        : null}
                                >
                                    <FormLabel className={cn(!changeName && 'tw-opacity-50')}>
                                        {i18next.t('common.name').capitalize()}
                                    </FormLabel>
                                    <FormInput
                                        {...field}
                                        disabled={!changeName}
                                        onChange={e => {
                                            const val = e.target.value;
                                            field.onChange(val);
                                            if (onChangeField) {
                                                onChangeField('name')(val);
                                            }
                                        }}
                                        placeholder={!changeName ? i18next.t('alarms.keep_original', 'Keep original values') : ''}
                                    />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="tw-pb-2.5">
                        <FormField
                            control={form.control}
                            name="changeName"
                            render={({ field }) => (
                                <FormItem>
                                    <Switch
                                        checked={field.value}
                                        label={i18next.t('common.change', 'Change?')}
                                        onCheckedChange={checked => {
                                            field.onChange(checked);
                                            const newValue = checked ? (mostCommonName || '') : '';
                                            setValue('name', newValue);
                                            if (onChangeField) {
                                                onChangeField('name')(newValue);
                                                onChangeField('changeName')(checked);
                                            }
                                        }}
                                    />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>

                {/* Notifications Property */}
                <div className="tw-flex tw-items-end tw-gap-4">
                    <div className="tw-flex-1">
                        <FormField
                            control={form.control}
                            name="userIds"
                            render={({ field }) => (
                                <FormItem itemInfo={
                                    field.value.length === 0 && changeUserIds
                                        ? i18next.t('alarms.notifications_warning').capitalize()
                                        : null}
                                >
                                    <FormLabel className={cn(!changeUserIds && 'tw-opacity-50')}>
                                        {i18next.t('common.notifications').capitalize()}
                                    </FormLabel>
                                    <MultiSelect
                                        defaultValue={field.value}
                                        disabled={!changeUserIds}
                                        onValueChange={vals => {
                                            field.onChange(vals);
                                            if (onChangeField) {
                                                onChangeField('userIds')(vals);
                                            }
                                        }}
                                        options={userOptions}
                                        placeholder={!changeUserIds ? i18next.t('alarms.keep_original', 'Keep original values') : ''}
                                        variant="secondary"
                                    />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="tw-pb-2.5">
                        <FormField
                            control={form.control}
                            name="changeUserIds"
                            render={({ field }) => (
                                <FormItem>
                                    <Switch
                                        checked={field.value}
                                        label={i18next.t('common.change', 'Change?')}
                                        onCheckedChange={checked => {
                                            field.onChange(checked);
                                            const newValue = checked ? (mostCommonUserIds || []) : [];
                                            setValue('userIds', newValue);
                                            if (onChangeField) {
                                                onChangeField('userIds')(newValue);
                                                onChangeField('changeUserIds')(checked);
                                            }
                                        }}
                                    />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
            </div>
        </Form>
    );
}
